// /groups — the caller's groups, plus the create-group modal.
//
// The create form rendered only name + description until 2026-08-21, while the
// API had accepted five fields the whole time. Every group made in that window
// silently took members_open, requireApproval=false, and the raw browser zone.
// See docs/migration-gaps.md item 3; the fields and wording here come from the
// PHP original at app/site/dashboard/groups/list.php.

import { ApiClientError, createGroup, listGroups } from '../api/client.js';
import { navigate, onLeave, type View } from '../router.js';
import { el } from '../dom.js';
import {
  POST_PERMISSION_CHOICES,
  postPermissionLabel,
  type GroupSummary,
  type PostPermission,
} from '@msc/shared';
import { modal } from './modal.js';
import { eventTypePicker } from './eventTypePicker.js';
import { tzPicker } from './tzPicker.js';
import { viewerZone } from '../viewerZone.js';

export const groupsView: View = async (outlet) => {
  const main = el('main', { class: 'page' }, [el('p', { class: 'muted' }, 'Loading')]);
  outlet.append(main);

  let groups: GroupSummary[];
  try {
    groups = await listGroups();
  } catch (err) {
    if (err instanceof ApiClientError && err.status === 401) {
      navigate('/login', true);
      return;
    }
    main.replaceChildren(el('p', { class: 'form-error' }, 'Could not load your groups.'));
    return;
  }

  // The viewer's own zone is the sensible default for a group they are creating,
  // and a better guess than the raw browser zone: it is their saved account zone
  // when they have one. It is only ever the default OFFERED when authoring
  // events, never a display fallback, so guessing here is safe.
  const defaultZone = await viewerZone();
  const create = createGroupModal(defaultZone);

  // The modal lives on <body> so the router cannot tear it out mid-interaction,
  // which means it also does not get cleaned up by the outlet being replaced.
  onLeave(() => create.destroy());

  const newGroupBtn = el('button', { type: 'button', class: 'btn' }, '+ Create group');
  newGroupBtn.addEventListener('click', () => create.open());

  main.replaceChildren(
    el('div', { class: 'page-head' }, [
      el('h1', {}, 'Your groups'),
      // A link rather than a second primary button: creating a group is what
      // this page is for, and finding one is the occasional errand.
      el('div', { class: 'page-head-actions' }, [
        el('a', { class: 'btn btn-link', href: '/groups/find' }, 'Find a group'),
        newGroupBtn,
      ]),
    ]),
    groups.length ? groupList(groups) : emptyState(() => create.open()),
  );
};

function groupList(groups: GroupSummary[]): HTMLElement {
  return el(
    'ul',
    { class: 'card-list' },
    groups.map((g) =>
      el('li', { class: 'card' }, [
        el('a', { class: 'card-title', href: `/groups/${g.groupId}` }, g.name),
        // A suspended group stays in the member's own list. Hiding it would just
        // make a group they belong to vanish with no explanation; the badge is
        // the whole point of keeping it visible.
        ...(g.isSuspended ? [el('span', { class: 'badge badge-suspended' }, 'Suspended')] : []),
        el(
          'p',
          { class: 'muted' },
          `${g.memberCount} ${g.memberCount === 1 ? 'member' : 'members'} · you are ${g.myRoleLabel}`,
        ),
      ]),
    ),
  );
}

function emptyState(onCreate: () => void): HTMLElement {
  const btn = el('button', { type: 'button', class: 'btn btn-standalone' }, 'Create your first group');
  btn.addEventListener('click', onCreate);
  return el('section', { class: 'card' }, [
    el('p', { class: 'muted' }, 'You are not in any groups yet.'),
    btn,
  ]);
}

interface CreateModal {
  open(): void;
  destroy(): void;
}

function createGroupModal(defaultZone: string): CreateModal {
  const error = el('p', { class: 'form-error', hidden: true });
  const submit = el('button', { type: 'submit', class: 'btn' }, 'Create group');
  const cancel = el('button', { type: 'button', class: 'btn btn-secondary' }, 'Cancel');

  // autocorrect/spellcheck OFF: a group name is a label the user chose, not
  // prose. In the iOS shell autocorrect silently rewrote a typed `zz-app-test`
  // to `As-app-test` (found 2026-08-22) — it rewrites on word boundaries, so
  // any name with an unfamiliar token is fair game and the user may not notice.
  // `autocapitalize` is deliberately LEFT ALONE: capitalising the first letter
  // of a group name is wanted. Note `spellcheck` must be the STRING 'false'
  // — `el()` drops an attribute whose value is boolean false.
  //
  // ⚠ NO PLACEHOLDER, removed 2026-09-10 at Will's request. It used to read
  // "Thursday Poker", which told every non-poker group what this app assumed it
  // was for — and the app now ships Fasting, Workout and Faith types. An example
  // in a name field is a suggestion, and a wrong suggestion is worse than none.
  //
  // The wish behind the request was a placeholder that follows the event types
  // ticked in this same dialog. That is NOT a one-liner and is deliberately not
  // done here: `EVENT_TYPES[key].label` is not name-shaped ("Fast (multi-day,
  // shows a countdown)"), so it needs a new `groupNameExample` field on
  // `EventTypeDef`, and `eventTypePicker()` exposes only `element` and `value()`
  // with no change hook to drive it. See docs/todo-mac-hand-test-2026-09-10.md
  // item 1.
  const name = el('input', {
    id: 'ng-name',
    name: 'name',
    type: 'text',
    required: true,
    maxlength: 191,
    autocorrect: 'off',
    spellcheck: 'false',
  }) as HTMLInputElement;

  const description = el('textarea', {
    id: 'ng-description',
    name: 'description',
    rows: 3,
  }) as HTMLTextAreaElement;

  const permission = el('select', { id: 'ng-permission', name: 'postPermission' }) as HTMLSelectElement;
  for (const value of POST_PERMISSION_CHOICES) {
    const opt = el('option', { value }, postPermissionLabel(value)) as HTMLOptionElement;
    if (value === 'members_open') opt.selected = true;
    permission.append(opt);
  }

  // Nothing checked. A new group does plain events until its organizer opts
  // into something more specific, and `generic` is not in the list at all
  // because every group always has it. Changed 2026-09-02 from every-box-
  // checked; see docs/domain.md, "Event types, families, and the picker".
  const eventTypes = eventTypePicker({ selected: [], idPrefix: 'ng' });

  const approval = el('input', {
    id: 'ng-approval',
    name: 'requireApproval',
    type: 'checkbox',
  }) as HTMLInputElement;

  // Seeded from the viewer's zone, and `autodetect` covers the case where they
  // have none saved. The empty row means "use the site default".
  const zone = tzPicker({
    name: 'timeZone',
    value: defaultZone,
    inheritLabel: 'Site default',
    inheritHint: defaultZone,
    autodetect: true,
    id: 'ng-timezone',
  });

  // method="post" even though JS submits it: if the script fails, the browser
  // must not fall back to GET and put the field values in the URL.
  // Flat label/field pairs, matching every other form in the SPA: `label` is a
  // block carrying its own margins, so wrapper divs would only fight it.
  const form = el('form', { method: 'post', action: '/groups' }, [
    el('label', { for: 'ng-name' }, 'Group name'),
    name,
    el('label', { for: 'ng-description' }, [
      'Description ',
      el('span', { class: 'muted small' }, '(optional)'),
    ]),
    description,
    el('label', {}, 'Which event types does this group offer?'),
    eventTypes.element,
    el(
      'p',
      { class: 'muted small' },
      'Plain events are always available. Change this anytime in Manage. Narrowing or opening back up never touches existing events.',
    ),
    el('label', { for: 'ng-permission' }, 'Who can post photos & content?'),
    permission,
    // Photos, not posts. The PHP label said "posts", but group_posts has no
    // approved column in either schema — only photos can ever be held pending.
    // See docs/migration-gaps.md.
    el('label', { class: 'check', for: 'ng-approval' }, [
      approval,
      ' Require approval before photos go live',
    ]),
    el('label', { for: 'ng-timezone' }, 'Group timezone'),
    zone.root,
    el(
      'p',
      { class: 'muted small' },
      'Event times default to this zone. Members see them converted to their own. Change it anytime in Manage.',
    ),
    error,
    el('div', { class: 'form-actions' }, [submit, cancel]),
  ]) as HTMLFormElement;

  const dialog = modal({
    title: 'Create a group',
    body: form,
    onClose: () => {
      // Reset clears the fields and, via the picker's own reset handler, resyncs
      // its search box. Without it a cancelled attempt is still sitting there on
      // the next open.
      form.reset();
      zone.set(defaultZone);
      error.hidden = true;
      submit.removeAttribute('disabled');
    },
  });

  cancel.addEventListener('click', () => dialog.close());

  form.addEventListener('submit', async (ev) => {
    ev.preventDefault();
    error.hidden = true;

    // The form carries no `novalidate`, so `required` still fires — but the
    // browser will not check a field it considers empty-after-trim, and a name of
    // only spaces would reach the server as a 400. Check it here.
    const trimmedName = name.value.trim();
    if (trimmedName === '') {
      error.textContent = 'Give the group a name.';
      error.hidden = false;
      name.focus();
      return;
    }
    submit.setAttribute('disabled', 'disabled');
    try {
      const group = await createGroup({
        name: trimmedName,
        description: description.value.trim() || undefined,
        postPermission: permission.value as PostPermission,
        requireApproval: approval.checked,
        // '' means the site default; the API treats an absent zone the same way.
        timeZone: zone.value() || undefined,
        allowedEventTypes: eventTypes.value(),
      });
      dialog.close();
      navigate(`/groups/${group.groupId}`);
    } catch (err) {
      error.textContent =
        err instanceof ApiClientError ? err.message : 'Something went wrong. Try again.';
      error.hidden = false;
      submit.removeAttribute('disabled');
    }
  });

  return { open: dialog.open, destroy: dialog.destroy };
}
